import {
  AloftDate,
  AloftPrice,
  PolygonGeoSearch,
  SubjectData,
  isDefined,
  orUndefined,
  schAloftArea,
} from '@aloft/toolkit-shared';

import { useState } from 'react';

import { InitialCompSearch } from '../types.ts';
import { useRecommendedCompsForm } from './useRecommendedCompsForm.ts';

const glaThresholdMult = 0.2;
// const lotSizeThresholdMult = 0.2;

export const useRecommendedCompsInternalState = (
  subject: SubjectData,
  effectiveDate: AloftDate,
) => {
  const form = useRecommendedCompsForm({
    search: {
      filters: {},
      geoSearch: {
        t: 'polygon',
        coords: [],
      },
      matchCriteria: {},
    },
    originalSearch: {
      filters: {},
      geoSearch: {
        t: 'polygon',
        coords: [],
      },
      matchCriteria: {},
    },
  });

  const getGeoSearch = () => {
    const geoSearch = form.getValues('search.geoSearch');
    if (geoSearch.t !== 'polygon') {
      throw new Error('Radius search unsupported');
    }
    return geoSearch;
  };

  const setGeoSearch = (geoSearch: PolygonGeoSearch) => {
    form.setValue('search.geoSearch', geoSearch);
  };

  const getDefaultFormValues = () => {
    const gla = orUndefined((v) => schAloftArea.parse(v))(subject.editableData.improvements.gla);
    // const lotSize = orUndefined((v) => schAloftArea.parse(v))(subject.editableData.site.area);
    const { age } = subject.editableData.improvements;

    let glaMin;
    let glaMax;
    if (isDefined(gla)) {
      const glaThreshold = gla.mul(glaThresholdMult);
      glaMin = gla.sub(glaThreshold);
      glaMax = gla.add(glaThreshold);
    }

    let lotSizeMin;
    let lotSizeMax;

    // if (isDefined(lotSize)) {
    //   const lotSizeThreshold = lotSize.mul(lotSizeThresholdMult);
    //   lotSizeMin = lotSize.sub(lotSizeThreshold);
    //   lotSizeMax = lotSize.add(lotSizeThreshold);
    // }

    let yearBuiltRange;
    if (isDefined(age) && age < 10) {
      const yearBuiltMax = effectiveDate.getYear();
      const yearBuiltMin = yearBuiltMax - 20;
      yearBuiltRange = {
        max: yearBuiltMax,
        min: yearBuiltMin,
      };
    }

    const maxSaleDate = effectiveDate;

    let search = {} as InitialCompSearch['body']['search'];

    search = {
      filters: {
        propertySubType: subject.editableData.improvements.propertyType,
        saleDate: {
          max: maxSaleDate,
          min: maxSaleDate.subtractMonths(12),
        },
        gla: {
          max: glaMax,
          min: glaMin,
        },
        lotSize: {
          max: undefined,
          min: undefined,
        },
        yearBuilt: yearBuiltRange,
        salePrice: {
          min: AloftPrice.fromUnitAmount(1000000),
          max: undefined,
        },
      },
      geoSearch: {
        t: 'polygon',
        coords: [],
      },
      matchCriteria: {
        saleDate: 'min_max_range',
        salePrice: 'min_max_range',
        gla: 'min_max_range',
        lotSize: 'min_max_range',
        yearBuilt: 'min_max_range',
        bedrooms: 'min_max_range',
        fullBaths: 'min_max_range',
        stories: 'min_max_range',
        garageSpaces: 'min_max_range',
      },
    };

    return search;
  };

  const [showNoRecommendations, setShowNoRecommendations] = useState(false);

  return {
    form,
    getGeoSearch,
    setGeoSearch,
    showNoRecommendations,
    setShowNoRecommendations,
    getDefaultFormValues,
  };
};
